import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { FaFacebookF, FaRedditAlien, FaTwitter } from 'react-icons/fa'
import { LogoMark } from '../components/Logo'
import Icon from '../components/Icons'
import { company, services, navLinks } from '../data/site'

const socials = [
  { label: 'X', href: 'https://x.com/AldaronLimited', Glyph: FaTwitter },
  {
    label: 'Reddit',
    href: 'https://www.reddit.com/u/ALDARON_LIMITED/s/EMZf4fX9V6',
    Glyph: FaRedditAlien,
  },
  { label: 'Facebook', href: 'https://www.facebook.com/share/1GEQ9aRY1G/', Glyph: FaFacebookF },
]

const Footer = () => {
  const [email, setEmail] = useState('')
  const [subscribed, setSubscribed] = useState(false)

  // No mailing-list backend yet — hand off to email so the control is not a dead end.
  const handleSubscribe = (e) => {
    e.preventDefault()
    if (!email) return
    window.location.href = `mailto:${company.emails[0]}?subject=${encodeURIComponent(
      'Newsletter subscription'
    )}&body=${encodeURIComponent(`Please subscribe ${email} to the Aldaron newsletter.`)}`
    setSubscribed(true)
  }

  return (
    <footer className="relative overflow-hidden border-t border-ink-950/10 bg-paper-100">
      <div className="container">
        {/* Oversized wordmark sets the scale, the way a printed colophon would */}
        <div className="pt-20 md:pt-28">
          <div className="flex items-end justify-between gap-8">
            <p className="font-display text-[clamp(3rem,13vw,11rem)] font-semibold leading-[0.82] tracking-tightest text-ink-950">
              ALDARON LIMITED
            </p>
            <LogoMark className="mb-2 hidden h-16 w-16 shrink-0 text-ink-950 md:block" />
          </div>
          <p className="mt-6 font-serif text-xl italic text-ink-400">{company.tagline}</p>
        </div>

        <div className="mt-20 grid gap-12 border-t border-ink-950/10 pt-14 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="max-w-xs text-sm leading-relaxed text-ink-500">{company.summary}</p>
            <div className="mt-8 flex items-center gap-2">
              {socials.map(({ label, href, Glyph }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  className="flex h-10 w-10 items-center justify-center rounded-full text-ink-500 ring-1 ring-inset ring-ink-950/12 transition-all duration-500 ease-out-expo hover:bg-ink-950 hover:text-paper-100 hover:ring-ink-950"
                >
                  <Glyph className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>

          <nav aria-label="Footer">
            <p className="label">Company</p>
            <ul className="mt-6 space-y-3">
              {navLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-sm text-ink-500 transition-colors hover:text-ink-950"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <p className="label">Services</p>
            <ul className="mt-6 space-y-3">
              {services.map((service) => (
                <li key={service.slug}>
                  <Link
                    to="/services"
                    className="text-sm text-ink-500 transition-colors hover:text-ink-950"
                  >
                    {service.short}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="label">Get in touch</p>
            <ul className="mt-6 space-y-3">
              {company.phones.map((phone) => (
                <li key={phone}>
                  <a
                    href={`tel:${phone.replace(/\s/g, '')}`}
                    className="text-sm text-ink-500 transition-colors hover:text-ink-950"
                  >
                    {phone}
                  </a>
                </li>
              ))}
              <li>
                <a
                  href={`mailto:${company.emails[0]}`}
                  className="break-all text-sm text-ink-500 transition-colors hover:text-ink-950"
                >
                  {company.emails[0]}
                </a>
              </li>
            </ul>

            <form onSubmit={handleSubscribe} className="mt-8">
              <label htmlFor="newsletter" className="label">
                Newsletter
              </label>
              <div className="mt-3 flex items-center border-b border-ink-950/20 focus-within:border-ink-950">
                <input
                  id="newsletter"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  className="min-w-0 flex-1 bg-transparent py-3 text-sm text-ink-950 placeholder:text-ink-300 focus:outline-none"
                />
                <button
                  type="submit"
                  aria-label="Subscribe"
                  className="shrink-0 p-2 text-ink-950 transition-transform duration-500 ease-out-expo hover:translate-x-1"
                >
                  <Icon name="arrow" className="h-4 w-4" />
                </button>
              </div>
              {subscribed && (
                <p className="mt-3 text-xs text-accent-500">Opening your mail app…</p>
              )}
            </form>
          </div>
        </div>

        <div className="flex flex-col items-start justify-between gap-3 border-t border-ink-950/10 py-8 text-xs text-ink-400 sm:flex-row sm:items-center">
          <p>
            &copy; {new Date().getFullYear()} {company.name}. All rights reserved.
          </p>
          <p className="flex items-center gap-4">
            <span className="transition-colors hover:text-ink-950">Terms &amp; Conditions</span>
            <span aria-hidden="true" className="text-ink-300">
              /
            </span>
            <span className="transition-colors hover:text-ink-950">Privacy Policy</span>
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
